/**
 * UserPill — small header element showing the current sign-in state.
 *
 *   anonymous → "Sign in" button (opens SignInModal)
 *   authed    → email/displayName + Pro badge + dropdown with Sign out
 *
 * Hydrates auth state on mount, and again when the page loads with
 * `?signed_in=1` in the URL (post-magic-link redirect).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SignInModal } from "@/components/SignInModal";
import { ProfileModal } from "@/components/ProfileModal";
import { useAuthStore } from "@/store/authStore";
import { flag, flagUrl } from "@/lib/countries";

export function UserPill() {
  const { t } = useTranslation();
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const loadMe = useAuthStore((s) => s.loadMe);
  const signOut = useAuthStore((s) => s.signOut);

  const [signInOpen, setSignInOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileMode, setProfileMode] = useState<"settings" | "welcome">("settings");
  const [menuOpen, setMenuOpen] = useState(false);
  const [postSignInToast, setPostSignInToast] = useState<string | null>(null);

  // Initial load — and detect the post-magic-link redirect.
  useEffect(() => {
    void loadMe();
    const url = new URL(window.location.href);
    if (url.searchParams.get("signed_in") === "1") {
      setPostSignInToast("Signed in");
      url.searchParams.delete("signed_in");
      window.history.replaceState({}, "", url.toString());
    } else {
      const errCode = url.searchParams.get("auth_error");
      if (errCode) {
        setPostSignInToast(
          errCode === "token_expired"
            ? "Sign-in link expired"
            : errCode === "token_used"
              ? "That link was already used"
              : "Sign-in failed",
        );
        url.searchParams.delete("auth_error");
        window.history.replaceState({}, "", url.toString());
      }
    }
  }, [loadMe]);

  // Auto-clear the post-sign-in toast.
  useEffect(() => {
    if (!postSignInToast) return;
    const t = window.setTimeout(() => setPostSignInToast(null), 2500);
    return () => window.clearTimeout(t);
  }, [postSignInToast]);

  // Welcome-mode country picker: auto-open ProfileModal when a user is
  // signed in but has not yet picked a country (countryCode === "XX").
  // Single-shot per user — once the user picks OR dismisses, we set a
  // localStorage flag so we never nag them again. They can still change
  // country any time via the UserPill → Profile menu.
  //
  // This is the May-15-launch fix for the "Indian users sign in and never
  // discover the country picker" gap.
  useEffect(() => {
    if (status !== "authed" || !user) return;
    if (user.countryCode && user.countryCode !== "XX") return;
    const flagKey = `curvle.welcomeCountryShown:${user.id}`;
    try {
      if (window.localStorage.getItem(flagKey) === "1") return;
    } catch {
      // localStorage unavailable (private mode) — fall through and show
      // the modal anyway. Worst case: shown twice on subsequent loads.
    }
    // Defer slightly so we don't race the post-sign-in toast or the page
    // settling after the magic-link redirect.
    const t = window.setTimeout(() => {
      setProfileMode("welcome");
      setProfileOpen(true);
      try {
        window.localStorage.setItem(flagKey, "1");
      } catch { /* noop */ }
    }, 600);
    return () => window.clearTimeout(t);
  }, [status, user]);

  const closeProfile = () => {
    setProfileOpen(false);
    // Always reset to settings after close, so subsequent UserPill →
    // Profile clicks don't re-render in welcome mode.
    setProfileMode("settings");
  };

  const label =
    status === "authed" && user
      ? user.displayName ?? user.email ?? t("header.signed_in_default_label")
      : t("header.sign_in");

  return (
    <>
      {status === "authed" && user ? (
        <div className="relative">
          <button
            type="button"
            className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs hover:bg-white/20"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {flagUrl(user.countryCode, 40) ? (
              <img
                src={flagUrl(user.countryCode, 40)!}
                width={18}
                height={13}
                alt=""
                className="rounded-[2px] shadow-[0_0_0_1px_rgba(255,255,255,0.15)]"
              />
            ) : (
              <span aria-hidden="true" className="text-sm">{flag(user.countryCode)}</span>
            )}
            <span className="max-w-[10rem] truncate">{label}</span>
            {user.founderNumber != null ? (
              <span className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-950">
                {t("founder.badge_short")}
              </span>
            ) : user.isPro ? (
              <span className="rounded-full bg-amber-300/90 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-950">
                Pro
              </span>
            ) : null}
          </button>
          {menuOpen && (
            <>
              {/* Click-anywhere-else closer. */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setMenuOpen(false)}
              />
              <div className="absolute right-0 top-full z-50 mt-2 w-48 rounded-xl bg-emerald-950/95 p-2 text-sm shadow-xl ring-1 ring-white/10">
                <div className="truncate px-3 py-2 text-xs opacity-60">{user.email}</div>
                <button
                  type="button"
                  className="block w-full rounded-lg px-3 py-2 text-left hover:bg-white/10"
                  onClick={() => {
                    setMenuOpen(false);
                    setProfileMode("settings");
                    setProfileOpen(true);
                  }}
                >
                  {t("user_menu.profile")}
                </button>
                <a
                  href="/leaderboard"
                  className="block w-full rounded-lg px-3 py-2 text-left hover:bg-white/10"
                  onClick={() => setMenuOpen(false)}
                >
                  Leaderboard
                </a>
                <a
                  href="/leagues"
                  className="block w-full rounded-lg px-3 py-2 text-left hover:bg-white/10"
                  onClick={() => setMenuOpen(false)}
                >
                  {t("user_menu.friend_leagues")}
                </a>
                <a
                  href="/pricing"
                  className="block w-full rounded-lg px-3 py-2 text-left hover:bg-white/10"
                  onClick={() => setMenuOpen(false)}
                >
                  Pricing
                </a>
                <a
                  href="/account"
                  className="block w-full rounded-lg px-3 py-2 text-left hover:bg-white/10"
                  onClick={() => setMenuOpen(false)}
                >
                  {t("user_menu.account_billing")}
                </a>
                {user.isAdmin && (
                  <a
                    href="/admin"
                    className="block w-full rounded-lg px-3 py-2 text-left text-amber-200 hover:bg-amber-300/10"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("user_menu.admin_dashboard")}
                  </a>
                )}
                <button
                  type="button"
                  className="block w-full rounded-lg px-3 py-2 text-left hover:bg-white/10"
                  onClick={async () => {
                    setMenuOpen(false);
                    await signOut();
                  }}
                >
                  {t("user_menu.sign_out")}
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Pick country"
            className="rounded-full bg-white/10 px-2 py-1.5 text-sm hover:bg-white/20"
            onClick={() => {
              setProfileMode("settings");
              setProfileOpen(true);
            }}
          >
            🌐
          </button>
          <button
            type="button"
            className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20"
            onClick={() => setSignInOpen(true)}
          >
            {status === "unknown" ? "…" : t("header.sign_in")}
          </button>
        </div>
      )}

      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      <ProfileModal open={profileOpen} onClose={closeProfile} mode={profileMode} />

      {postSignInToast && (
        <div
          className="pointer-events-none fixed left-1/2 top-4 z-40 -translate-x-1/2 rounded-full bg-emerald-700 px-4 py-1.5 text-sm font-semibold shadow-lg"
          role="status"
          aria-live="polite"
        >
          {postSignInToast}
        </div>
      )}
    </>
  );
}
